import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Alert from '@mui/material/Alert';
import { userAPI } from '../../actions/imsServer';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import ContentArea from '../../components/contentArea';
import Popup from '../../components/popup';
import FormLabel from '../../components/formItem/formLabel';
import Button from '../../components/button';
import Toggle from '../../components/toggle';
import InputField from '../../components/formItem/inputField';
import { PhotoEmpty, Camera, Tos } from '../../assets/images';
import { USER_GENDERS } from '../../constants';
import { formatDateTimeDisplay, getBirthDisplayValue, parseLocaleDateToYyyymmdd } from '../../utils/base';

export const UserMypageEdit = props => {
    const { t, i18n } = useTranslation();
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();
    const theme = useTheme();

    const [popupOpen, setPopupOpen] = useState(false);
    const [popupMessage, setPopupMessage] = useState('');
    const [popupSubMessage, setPopupSubMessage] = useState('');
    const [profileMenuOpen, setProfileMenuOpen] = useState(false);
    const [birthFocused, setBirthFocused] = useState(false);
    const [isCameraOpen, setIsCameraOpen] = useState(false);
    const [cameraStream, setCameraStream] = useState(null);
    const [cameraError, setCameraError] = useState(null);
    const [isImageValid, setIsImageValid] = useState(true);
    const [profileFile, setProfileFile] = useState(null);
    const [, setMeInfo] = useState(JSON.parse(localStorage.getItem('meInfo')));

    const [formData, setFormData] = useState({
        id: 0,
        loginId: '',
        name: '',
        birth: '',
        gender: 0,
        profilePath: '',
    });

    // profilePath가 변경되면 이미지 유효성을 리셋
    useEffect(() => {
        setIsImageValid(true);
    }, [formData.profilePath]);

    useEffect(() => {
        // Load initial user data from localStorage (mount only)
        const stored = JSON.parse(localStorage.getItem('meInfo'));
        setMeInfo(stored);
        try {
            if (stored) {
                const initialPath = stored.profilePath || '';
                setFormData({
                    id: stored.id || 0,
                    loginId: stored.loginId || '',
                    name: stored.name || '',
                    birth: stored.birth,
                    gender: stored.gender !== undefined ? stored.gender : 0,
                    profilePath: initialPath,
                });
            }
        } catch (error) {
            console.error('Failed to load user data from localStorage:', error);
        }
    }, [setFormData, setProfileFile]);

    // 팝업 표시 함수 (uploadProfileImage 및 message listener useEffect보다 먼저 정의)
    const showPopup = (message, subMessage = '') => {
        setPopupMessage(message);
        setPopupSubMessage(subMessage);
        setPopupOpen(true);
    };

    const uploadProfileImage = async file => {
        try {
            let fileToUse = file;

            // React Native에서 받은 이미지 데이터 처리
            if (file.uri) {
                // Base64 데이터가 있는 경우 Blob으로 변환, 없는 경우 URI를 직접 사용
                let reactNativeFile;

                if (file.data) {
                    let base64Data = file.data;
                    if (typeof base64Data === 'string' && base64Data.startsWith('data:')) {
                        const comma = base64Data.indexOf(',');
                        if (comma !== -1) {
                            base64Data = base64Data.slice(comma + 1);
                        }
                    }
                    const byteCharacters = atob(base64Data);
                    const byteNumbers = new Array(byteCharacters.length);
                    for (let i = 0; i < byteCharacters.length; i++) {
                        byteNumbers[i] = byteCharacters.charCodeAt(i);
                    }
                    const byteArray = new Uint8Array(byteNumbers);
                    const blob = new Blob([byteArray], {
                        type: file.type || 'image/jpeg',
                    });

                    // Blob을 File 객체로 변환
                    reactNativeFile = new File(
                        [blob],
                        file.name || 'image.jpg',
                        { type: file.type || 'image/jpeg' }
                    );
                    fileToUse = reactNativeFile;
                } else {
                    // URI만 있는 경우 fetch로 이미지 데이터 가져오기
                    try {
                        const response = await fetch(file.uri);
                        const blob = await response.blob();
                        reactNativeFile = new File(
                            [blob],
                            file.name || 'image.jpg',
                            { type: file.type || 'image/jpeg' }
                        );
                        fileToUse = reactNativeFile;
                    } catch (fetchError) {
                        console.error(
                            'Failed to fetch image from URI:',
                            fetchError
                        );
                        throw new Error(t('cannot_fetch_image_data'));
                    }
                }
            }

            // 파일 상태에 저장 (저장하기 버튼을 눌렀을 때 업로드하기 위해)
            setProfileFile(fileToUse);

            // 미리보기 URL 생성
            const tempUrl = URL.createObjectURL(fileToUse);
            setFormData(prev => ({
                ...prev,
                profilePath: tempUrl,
            }));
        } catch (error) {
            console.error('Failed to upload profile image:', error);
            showPopup(t('failed_to_upload_profile_image'));
        }
    };

    // Add message listener for React Native WebView
    useEffect(() => {
        const isReactNative = typeof window.ReactNativeWebView !== 'undefined';

        // Only add message listener in React Native environment
        if (!isReactNative) {
            return;
        }

        const handleMessage = async event => {
            try {
                const data = JSON.parse(event.data);

                if (data.type === 'imageSelected' && data.file) {
                    await uploadProfileImage(data.file);
                } else if (data.type === 'cameraResult' && data.file) {
                    // 카메라로 촬영한 이미지 처리
                    if (data.file.uri || data.file.data) {
                        await uploadProfileImage(data.file);
                    } else {
                        console.error(
                            'Camera result missing both URI and base64 data:',
                            data.file
                        );
                        showPopup(t('camera_capture_failed_try_again'));
                    }
                } else if (data.type === 'fileSelectionError') {
                    // 파일 선택 에러 메시지 표시
                    showPopup(data.message || t('file_selection_failed'));
                } else if (data.type === 'profilePhotoCameraClosed') {
                    // 프로필 사진 카메라가 닫혔을 때 (특별한 처리가 필요 없으면 아무것도 하지 않음)
                    console.log('Profile photo camera closed');
                }
            } catch (error) {
                console.error(
                    'Failed to handle message from React Native:',
                    error
                );
                showPopup(t('failed_to_upload_profile_photo_message'));
            }
        };

        // 여러 방법으로 메시지 수신 시도
        window.addEventListener('message', handleMessage);

        // React Native WebView의 postMessage 이벤트도 수신
        if (window.ReactNativeWebView) {
            window.ReactNativeWebView.onMessage = handleMessage;
        }

        // 추가: 전역 이벤트 리스너도 등록
        document.addEventListener('message', handleMessage);

        // 전역 함수로 React Native에서 직접 호출할 수 있도록 설정
        window.handleReactNativeMessage = handleMessage;

        return () => {
            window.removeEventListener('message', handleMessage);
            document.removeEventListener('message', handleMessage);
            if (window.ReactNativeWebView) {
                window.ReactNativeWebView.onMessage = null;
            }
            delete window.handleReactNativeMessage;
        };
    }, [t, uploadProfileImage, showPopup]);

    // 컴포넌트 언마운트 시 카메라 스트림 정리
    useEffect(() => {
        return () => {
            if (cameraStream) {
                cameraStream.getTracks().forEach(track => track.stop());
            }
        };
    }, [cameraStream]);

    // formData가 변경될 때 profileFile 초기화 (더 이상 사용하지 않음)
    // useEffect(() => {
    //     setProfileFile(null);
    // }, [formData.id]);

    // 카메라 Dialog가 열릴 때 비디오 요소에 스트림 연결
    useEffect(() => {
        if (isCameraOpen && cameraStream) {
            const video = document.getElementById('camera-video');
            if (video && cameraStream) {
                video.srcObject = cameraStream;
                video.play().catch(err => {
                    console.error('비디오 재생 실패:', err);
                });
            }
        }
    }, [isCameraOpen, cameraStream]);

    // 팝업 닫기 함수
    const handleClosePopup = () => {
        setPopupOpen(false);
    };

    // 프로필 메뉴 열기
    const handleProfileMenuOpen = () => {
        setProfileMenuOpen(true);
    };

    const handleProfileMenuClose = () => {
        setProfileMenuOpen(false);
    };

    const handlePhotoLibrarySelect = () => {
        if (typeof window.ReactNativeWebView !== 'undefined') {
            window.ReactNativeWebView.postMessage(
                JSON.stringify({
                    type: 'requestPhotoLibrary',
                    userId: formData.id,
                })
            );
        } else {
            const input = document.createElement('input');
            input.type = 'file';
            input.accept = 'image/jpeg,image/jpg,image/png,image/gif,image/webp';
            input.onchange = async event => {
                const file = event.target.files[0];
                if (file) {
                    if (file.type.startsWith('image/')) {
                        await uploadProfileImage(file);
                    } else {
                        showPopup(t('only_image_files_can_be_selected'));
                    }
                }
            };
            input.click();
        }
        handleProfileMenuClose();
    };

    const handleTakePhoto = async () => {
        if (typeof window.ReactNativeWebView !== 'undefined') {
            window.ReactNativeWebView.postMessage(
                JSON.stringify({
                    type: 'requestCamera',
                    userId: formData.id,
                })
            );
        } else {
            try {
                if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
                    setCameraError(t('camera_not_supported_browser'));
                    return;
                }

                const stream = await navigator.mediaDevices.getUserMedia({
                    video: {
                        facingMode: 'user',
                        width: { min: 320, ideal: 640, max: 1280 },
                        height: { min: 240, ideal: 480, max: 720 },
                    },
                });

                setCameraStream(stream);
                setIsCameraOpen(true);
                setCameraError(null);

                setTimeout(() => {
                    const video = document.getElementById('camera-video');
                    if (video && stream) {
                        video.srcObject = stream;
                        video.play().catch(err => {
                            console.error('비디오 재생 실패:', err);
                        });
                    }
                }, 100);
            } catch (err) {
                console.error('카메라 접근 실패:', err);
                let errorMessage = t('failed_to_initialize_camera');
                if (err.name === 'NotAllowedError') {
                    errorMessage = t('camera_access_permission_is_required');
                } else if (err.name === 'NotFoundError') {
                    errorMessage = t('camera_not_found');
                } else if (err.name === 'NotReadableError') {
                    errorMessage = t('camera_is_in_use_by_another_app');
                }
                showPopup(errorMessage);
            }
        }
        handleProfileMenuClose();
    };

    const handleCameraClose = useCallback(() => {
        if (cameraStream) {
            cameraStream.getTracks().forEach(track => track.stop());
            setCameraStream(null);
        }
        setIsCameraOpen(false);
        setCameraError(null);
    }, [cameraStream]);

    const handleCameraCapture = useCallback(() => {
        if (!cameraStream) return;

        const video = document.getElementById('camera-video');
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');

        if (video && context) {
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
            context.drawImage(video, 0, 0);

            canvas.toBlob(
                async blob => {
                    if (blob) {
                        const file = new File([blob], 'camera-capture.jpg', {
                            type: 'image/jpeg',
                        });
                        await uploadProfileImage(file);
                    }
                },
                'image/jpeg',
                0.8
            );
        }

        handleCameraClose();
    }, [cameraStream, handleCameraClose, uploadProfileImage]);

    const handleBirthDateChange = (e) => {
        const value = e.target.value;
        const digitsOnly = value.replace(/[^\d]/g, '').slice(0, 8);
        const currentDigits = String(formData.birth ?? '').replace(/[^\d]/g, '');
        if (!value.trim()) {
            setFormData(prev => ({ ...prev, birth: '' }));
            return;
        }
        if (digitsOnly.length < currentDigits.length && currentDigits.length > 0) {
            let next;
            if (digitsOnly === currentDigits.slice(0, digitsOnly.length)) {
                next = digitsOnly;
            } else {
                const removed = currentDigits.length - digitsOnly.length;
                next =
                    removed > 0
                        ? currentDigits.slice(0, currentDigits.length - removed)
                        : digitsOnly;
            }
            setFormData(prev => ({ ...prev, birth: next }));
            return;
        }
        if (digitsOnly.length === 8) {
            const parsed = parseLocaleDateToYyyymmdd(digitsOnly, i18n.language);
            if (parsed) {
                setFormData(prev => ({ ...prev, birth: parsed }));
                return;
            }
        }
        setFormData(prev => ({ ...prev, birth: digitsOnly }));
    };

    const handleDeleteProfile = () => {
        // 미리보기 이미지와 저장된 파일 초기화
        setFormData(prev => ({
            ...prev,
            profilePath: '',
        }));
        setProfileFile(null);
    };

    const handleSave = async () => {
        setIsLoading(true);

        try {
            // 1. 프로필 이미지 처리
            // - 새 파일이 선택된 경우: file과 함께 전송
            // - 이미지가 삭제된 경우 (초기 이미지가 있었는데 현재 빈 문자열): file 없이 전송
            const profileFormData = {
                userId: formData.id,
            };

            // 새 파일이 선택된 경우에만 file 추가
            if (profileFile !== null) {
                profileFormData.file = profileFile;
            }

            await userAPI.uploadProfileImage(profileFormData);

            // 2. 사용자 정보 업데이트 (birth는 서버에 yyyymmdd 8자리로 전송)
            const birthParsed = parseLocaleDateToYyyymmdd(formData.birth, i18n.language);
            const birthYyyymmdd = birthParsed || String(formData.birth ?? '').replace(/[^\d]/g, '').slice(0, 8);
            const updateData = {
                id: formData.id,
                name: formData.name,
                birth: birthYyyymmdd.length === 8 ? birthYyyymmdd : formData.birth,
                gender: formData.gender,
            };

            await userAPI.updateUser(updateData);

            // 3. 업데이트된 사용자 정보 가져오기
            const userResponse = await userAPI.getMyInfo();
            localStorage.setItem(
                'meInfo',
                JSON.stringify(userResponse.data.user)
            );

            setIsLoading(false);
            navigate(-1);
            showPopup(t('user_information_has_been_successfully_updated'));
        } catch (err) {
            console.error('Failed to update user:', err);
            setIsLoading(false);
            showPopup(t('failed_to_update_user_information'));
        }
    };

    return (
        <ContentArea sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '40px',
            flex: '1 0 0',
            alignSelf: 'stretch',
        }}>
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '60px',
            }}>
                {/* Form Fields Container */}
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '10px',
                    width: '100%',
                    maxWidth: '100%',
                    boxSizing: 'border-box',
                    px: '15px',
                }}>
                    {/* 이름 (Name) */}
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        width: '100%',
                        minWidth: 0,
                    }}>
                        <Box sx={{ flex: '0 0 100px', minWidth: '100px' }}>
                            <FormLabel
                                text={t('name')}
                                required={true}
                                sx={{
                                    letterSpacing: '-0.42px',
                                    textAlign: 'left'
                                }}
                            />
                        </Box>
                        <InputField
                            value={formData.name}
                            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                            placeholder={t('name')}
                            sx={{ flex: 1, minWidth: 0, maxWidth: '100%' }}
                        />
                    </Box>

                    {/* 계정 (Account) */}
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '10px',
                        width: '100%',
                        minWidth: 0,
                    }}>
                        <Box sx={{ flex: '0 0 100px', minWidth: '100px' }}>
                            <FormLabel
                                text={t('account')}
                                required={true}
                                sx={{
                                    letterSpacing: '-0.42px',
                                    textAlign: 'left'
                                }}
                            />
                        </Box>
                        <Typography sx={{
                            display: 'flex',
                            height: '40px',
                            minWidth: 0,
                            padding: '0 15px',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            flex: 1
                        }}>
                            {formData.loginId}
                        </Typography>
                    </Box>

                    {/* 성별 (Gender) */}
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        width: '100%',
                        minWidth: 0,
                    }}>
                        <Box sx={{ flex: '0 0 100px', minWidth: '100px' }}>
                            <FormLabel
                                text={t('gender')}
                                required={true}
                                sx={{
                                    letterSpacing: '-0.42px',
                                    textAlign: 'left'
                                }}
                            />
                        </Box>
                        <Box sx={{ minWidth: 0, flex: 1, maxWidth: '235px' }}>
                            <Toggle
                                options={USER_GENDERS.map(gender => ({ value: gender.value, label: t(gender.label) }))}
                                value={formData.gender}
                                onChange={(index) => setFormData(prev => ({ ...prev, gender: index }))}
                            />
                        </Box>
                    </Box>

                    {/* 생년월일 (Birth Date) */}
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        width: '100%',
                        minWidth: 0,
                    }}>
                        <Box sx={{ flex: '0 0 100px', minWidth: '100px' }}>
                            <FormLabel
                                text={t('birth_date')}
                                required={true}
                                sx={{
                                    letterSpacing: '-0.42px',
                                    textAlign: 'left'
                                }}
                            />
                        </Box>
                        <InputField
                            value={
                                birthFocused
                                    ? String(formData.birth ?? '')
                                        .replace(/[^\d]/g, '')
                                        .slice(0, 8)
                                    : getBirthDisplayValue(formData.birth)
                            }
                            onChange={handleBirthDateChange}
                            onFocus={() => setBirthFocused(true)}
                            onBlur={() => setBirthFocused(false)}
                            placeholder={formatDateTimeDisplay('1990-03-15', 'date')}
                            inputProps={{
                                inputMode: 'numeric',
                                pattern: '[0-9]*',
                                maxLength: birthFocused ? 8 : 14,
                            }}
                            sx={{ flex: 1, minWidth: 0, maxWidth: '100%' }}
                            showClearIcon={false}
                        />
                    </Box>

                    {/* 프로필사진 (Photo Upload) */}
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        width: '100%',
                        minWidth: 0,
                    }}>
                        <Box sx={{ flex: '0 0 100px', minWidth: '100px' }}>
                            <FormLabel
                                text={t('profile_photo')}
                                sx={{
                                    letterSpacing: '-0.42px',
                                    textAlign: 'left'
                                }}
                            />
                        </Box>
                        <Box sx={{
                            display: 'flex',
                            padding: '20px 0',
                            alignItems: 'flex-end',
                            gap: '0',
                            flex: 1,
                            minWidth: 0,
                        }}>
                            <Box sx={{
                                width: '60px',
                                height: '60px',
                                cursor: formData.profilePath && formData.profilePath.trim() !== '' && isImageValid ? 'pointer' : 'default',
                                position: 'relative',
                            }}
                                onClick={formData.profilePath && formData.profilePath.trim() !== '' && isImageValid ? handleDeleteProfile : undefined}>
                                {(formData.profilePath && formData.profilePath.trim() !== '' && isImageValid) ? (
                                    <Box
                                        component="img"
                                        src={formData.profilePath}
                                        alt={formData.name}
                                        sx={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover',
                                            borderRadius: '50%'
                                        }}
                                        onError={() => setIsImageValid(false)}
                                    />
                                ) : (
                                    <PhotoEmpty
                                        color={theme.palette.figma.white[100]}
                                        size={60}
                                    />
                                )}
                            </Box>
                            <Box sx={{
                                width: '20px',
                                height: '20px',
                                aspectRatio: '1/1',
                                cursor: 'pointer',
                                marginLeft: '-10px',
                                marginBottom: '0px',
                                position: 'relative',
                                zIndex: 1,
                            }}
                                onClick={handleProfileMenuOpen}>
                                <Camera color={theme.palette.figma.icon} size={20} />
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </Box>

            {/* Action Buttons */}
            <Box sx={{
                display: 'flex',
                height: '114px',
                flexDirection: 'column',
                justifyContent: 'flex-end',
                alignItems: 'center',
                gap: '20px',
                flexShrink: '0',
                alignSelf: 'stretch',
            }}>
                <Box sx={{
                    display: 'flex',
                    padding: '0 20px',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: '10px',
                    alignSelf: 'stretch',
                }}>
                    <Button
                        variant="gray"
                        onClick={() => navigate(-1)}
                        sx={{ flex: 1 }}
                    >
                        {t('cancel')}
                    </Button>
                    <Button
                        variant="normal"
                        onClick={handleSave}
                        disabled={isLoading}
                        sx={{ flex: 1 }}
                    >
                        {t('save_button')}
                    </Button>
                </Box>
            </Box>

            {/* Profile Photo Menu Dialog */}
            <Dialog
                open={profileMenuOpen}
                onClose={handleProfileMenuClose}
                PaperProps={{
                    sx: {
                        borderRadius: '20px',
                        background: theme.palette.background.default,
                        display: 'flex',
                        width: '320px',
                        padding: '20px 0',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: '20px',
                    },
                }}
            >
                <DialogContent
                    sx={{
                        display: 'flex',
                        minHeight: '90px',
                        padding: '0px 20px',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '10px',
                        alignSelf: 'stretch',
                    }}
                >
                    <Box
                        onClick={handlePhotoLibrarySelect}
                        sx={{
                            display: 'flex',
                            padding: '16px 20px',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            alignSelf: 'stretch',
                            cursor: 'pointer',
                            borderRadius: '12px',
                            border: '1px solid ' + theme.palette.figma.white[10],
                            '&:hover': {
                                backgroundColor: theme.palette.figma.white[3],
                            },
                        }}
                    >
                        <Typography
                            sx={{
                                color: theme.palette.figma.white[100],
                                fontSize: '15px',
                                fontStyle: 'normal',
                                fontWeight: 500,
                                lineHeight: 'normal',
                                letterSpacing: '-0.45px',
                            }}
                        >
                            {t('photo_library')}
                        </Typography>
                        <Tos />
                    </Box>

                    <Box
                        onClick={handleTakePhoto}
                        sx={{
                            display: 'flex',
                            padding: '16px 20px',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            alignSelf: 'stretch',
                            cursor: 'pointer',
                            borderRadius: '12px',
                            border: '1px solid ' + theme.palette.figma.white[10],
                            '&:hover': {
                                backgroundColor: theme.palette.figma.white[3],
                            },
                        }}
                    >
                        <Typography
                            sx={{
                                color: theme.palette.figma.white[100],
                                fontSize: '15px',
                                fontStyle: 'normal',
                                fontWeight: 500,
                                lineHeight: 'normal',
                                letterSpacing: '-0.45px',
                            }}
                        >
                            {t('take_photo')}
                        </Typography>
                        <Camera />
                    </Box>
                </DialogContent>
            </Dialog>

            {/* Camera Dialog */}
            <Dialog
                open={isCameraOpen}
                onClose={handleCameraClose}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: '20px',
                        backgroundColor: theme.palette.figma.block,
                        padding: '20px'
                    }
                }}
            >
                <DialogTitle sx={{
                    color: theme.palette.figma.white[100],
                    fontSize: '16px',
                    fontWeight: 500,
                    letterSpacing: '-0.48px',
                    pb: 2
                }}>
                    {t('camera_title')}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ position: 'relative', width: '100%', paddingBottom: '56.25%' }}>
                        <video
                            id="camera-video"
                            autoPlay
                            playsInline
                            style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                borderRadius: '15px'
                            }}
                        />
                    </Box>
                    {cameraError && (
                        <Alert severity="error" sx={{ mt: 2 }}>
                            {cameraError}
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions sx={{ padding: '20px' }}>
                    <Button
                        variant="gray"
                        onClick={handleCameraClose}
                        sx={{
                            color: theme.palette.figma.white[100],
                            fontSize: '14px',
                            fontWeight: 500,
                        }}
                    >
                        {t('cancel')}
                    </Button>
                    <Button
                        variant="normal"
                        onClick={handleCameraCapture}
                        sx={{
                            fontSize: '14px',
                            fontWeight: 500,
                            borderRadius: '15px',
                            padding: '10px 20px',
                        }}
                    >
                        {t('camera_capture')}
                    </Button>
                </DialogActions>
            </Dialog>

            <Popup
                open={popupOpen}
                message={popupMessage}
                subMessage={popupSubMessage}
                onConfirm={handleClosePopup}
            />
        </ContentArea>
    );
};

export default UserMypageEdit;
